import { useMemo } from 'react';

import { Typography } from '@mui/material';

import { validatePreset } from './utils';
import { List } from '../../../lists/List';
import { ListItem } from '../../../lists/ListItem';
import { ListItemText } from '../../../lists/ListItemText';
import { ListSubheader } from '../../../lists/ListSubheader';
import { Tooltip } from '../../../tooltip';
import { useDatePickerContext } from '../../components/useDatePickerContext';
import { RangePickerProviderProps } from '../../types';
import { tokens } from '../../../../theme/generated/tokens';
import {
  buildPresetRange,
  isSelectedFilterPreset,
  rangeToUtc,
} from '../../utils';

export const FilterPresetsList = () => {
  const ctx = useDatePickerContext<RangePickerProviderProps>();

  const validatedFilterPresets = useMemo(() => {
    return ctx.filterPresets.map((preset) =>
      validatePreset(preset, {
        timezone: ctx.timezone,
        range: ctx.selected,
        startMonth: ctx.startMonth,
        endMonth: ctx.endMonth,
        maxNumberOfDays: ctx.maxNumberOfDays,
      })
    );
  }, [
    ctx.filterPresets,
    ctx.timezone,
    ctx.selected,
    ctx.startMonth,
    ctx.endMonth,
    ctx.maxNumberOfDays,
  ]);

  const minmaxConstraints = {
    minDate:
      typeof ctx.minDate === 'function'
        ? ctx.minDate({
            range: rangeToUtc(ctx.selected),
            timezone: ctx.timezone,
          })
        : ctx.minDate,
    maxDate:
      typeof ctx.maxDate === 'function'
        ? ctx.maxDate({
            range: rangeToUtc(ctx.selected),
            timezone: ctx.timezone,
          })
        : ctx.maxDate,
  };

  return (
    <List size="small" disablePadding testId="range-picker-filter-presets">
      <ListSubheader
        sx={{
          alignItems: 'center',
          padding: 8,
          color: tokens.theme.fg.structural.primary,
        }}
      >
        <Typography variant="P12M">Date range:</Typography>
      </ListSubheader>

      {validatedFilterPresets?.map((preset) => {
        return (
          <Tooltip
            key={preset.label}
            title={preset.error}
            wrapIf={!!preset.error}
            placement="top"
            disableInteractive
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -15],
                  },
                },
              ],
            }}
          >
            <span>
              <ListItem
                sx={{
                  borderRadius: tokens.theme.radius.sm,
                  padding: '7px 8px',
                }}
                disabled={!!preset.error}
                selected={
                  ctx.selected
                    ? isSelectedFilterPreset({
                        value: ctx.selected,
                        filter: preset,
                        timezone: ctx.timezone,
                        allowTimeInput: ctx.allowTimeInput,
                        constraints: minmaxConstraints,
                      })
                    : false
                }
                onClick={() => {
                  const { from, to } = buildPresetRange({
                    preset,
                    timezone: ctx.timezone,
                    allowTimeInput: ctx.allowTimeInput,
                    constraints: minmaxConstraints,
                  });
                  ctx.setSelected({ from, to });
                  if (from) {
                    ctx.pickerRef?.scrollToMonth(from);
                  }
                }}
                component="li"
                testId={'range-preset-' + preset.label}
              >
                <ListItemText
                  primary={preset.label}
                  primaryTypographyProps={{ variant: 'P12R' }}
                />
              </ListItem>
            </span>
          </Tooltip>
        );
      })}
    </List>
  );
};
